if (!customElements.get('hm-sticky-atc')) {
  customElements.define(
    'hm-sticky-atc',
    class HmStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.variantEl = this.querySelector('[data-hm-sticky-variant]');
        this.priceEl = this.querySelector('[data-hm-sticky-price]');
        this.button = this.querySelector('[data-hm-sticky-submit]');
        this.label = this.querySelector('[data-hm-sticky-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.mutationObserver = new MutationObserver(() => this.syncFromDom());
        this.mainAtcObserver = undefined;
        this.mainAtcSource = undefined;
        this.observedSubmitSource = undefined;
        this.loadingFallbackTimeout = undefined;

        this.handleClick = this.handleClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.handleCartUpdate = this.handleCartUpdate.bind(this);
        this.handleCartError = this.handleCartError.bind(this);

        this.button?.addEventListener('click', this.handleClick);
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.handleCartError);

        this.observeSources();
        this.syncFromDom();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleClick);
        this.variantUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.mutationObserver?.disconnect();
        this.mainAtcObserver?.disconnect();
        this.mainAtcObserver = undefined;
        this.mainAtcSource = undefined;
        this.observedSubmitSource = undefined;
        this.removeAttribute('data-main-atc-visible');
        this.clearLoadingFallback();
      }

      handleClick() {
        const mainSubmit = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!mainSubmit) {
          this.syncFromDom();
          return;
        }
        if (mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true') return;

        this.setLoading(true);
        this.setLoadingFallback();
        mainSubmit.click();
      }

      handleVariantChange(event) {
        if (event?.data?.sectionId !== this.sectionId) return;
        this.observeSources();
        this.syncFromDom(event.data.variant);
      }

      handleCartUpdate(event) {
        if (event?.source !== 'product-form') return;
        this.clearLoadingFallback();
        this.setLoading(false);
      }

      handleCartError(event) {
        if (event?.source !== 'product-form') return;
        this.clearLoadingFallback();
        this.setLoading(false);
        this.syncFromDom();
      }

      syncFromDom(variant) {
        const priceSource = document.getElementById(`price-${this.sectionId}`);
        const submitSource = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const submitLabel = submitSource?.querySelector('span');
        const isUnavailable = priceSource?.classList.contains('hidden');

        if (submitSource !== this.observedSubmitSource) this.observeSources();
        this.hidden = !submitSource;

        if (this.priceEl && priceSource) {
          this.priceEl.innerHTML = priceSource.innerHTML;
          this.priceEl.classList.toggle('hidden', Boolean(isUnavailable || !submitSource));
        }

        if (!submitSource) {
          this.clearLoadingFallback();
          this.setLoading(false);
          if (this.button) {
            this.button.disabled = true;
            this.button.setAttribute('aria-disabled', 'true');
          }
          return;
        }

        if (this.variantEl && variant?.public_title) {
          this.variantEl.textContent = variant.public_title;
        } else if (this.variantEl && variant?.title) {
          this.variantEl.textContent = variant.title;
        } else if (this.variantEl && isUnavailable && submitLabel) {
          this.variantEl.textContent = submitLabel.textContent.trim();
        }

        if (this.button && submitSource) {
          const isDisabled = submitSource.disabled || submitSource.getAttribute('aria-disabled') === 'true';
          this.button.disabled = isDisabled;
          if (isDisabled) {
            this.button.setAttribute('aria-disabled', 'true');
          } else {
            this.button.removeAttribute('aria-disabled');
          }
        }

        if (this.label && submitLabel) {
          this.label.textContent = submitLabel.textContent.trim();
        }
      }

      observeSources() {
        const priceSource = document.getElementById(`price-${this.sectionId}`);
        const submitSource = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.mutationObserver?.disconnect();
        if (priceSource) {
          this.mutationObserver.observe(priceSource, { attributes: true, childList: true, subtree: true });
        }
        if (submitSource) {
          this.mutationObserver.observe(submitSource, { attributes: true, childList: true, subtree: true });
        }
        this.observedSubmitSource = submitSource;
        this.observeMainAtcVisibility(submitSource);
      }

      observeMainAtcVisibility(submitSource) {
        if (submitSource === this.mainAtcSource && this.mainAtcObserver) {
          this.syncMainAtcVisibility(submitSource);
          return;
        }

        this.mainAtcObserver?.disconnect();
        this.mainAtcObserver = undefined;
        this.mainAtcSource = submitSource;
        this.setMainAtcVisible(false);

        if (!submitSource || !('IntersectionObserver' in window)) return;

        this.syncMainAtcVisibility(submitSource);
        this.mainAtcObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries.find((candidate) => candidate.target === this.mainAtcSource);
            if (entry) this.setMainAtcVisible(entry.isIntersecting);
          },
          { threshold: 0.01 }
        );
        this.mainAtcObserver.observe(submitSource);
      }

      syncMainAtcVisibility(submitSource) {
        const rect = submitSource?.getBoundingClientRect();
        const isVisible = Boolean(
          rect &&
            rect.width > 0 &&
            rect.height > 0 &&
            rect.bottom > 0 &&
            rect.right > 0 &&
            rect.top < window.innerHeight &&
            rect.left < window.innerWidth
        );
        this.setMainAtcVisible(isVisible);
      }

      setMainAtcVisible(isVisible) {
        this.toggleAttribute('data-main-atc-visible', Boolean(isVisible));
      }

      setLoading(isLoading) {
        this.button?.classList.toggle('loading', isLoading);
        this.button?.setAttribute('aria-busy', isLoading ? 'true' : 'false');
        this.spinner?.classList.toggle('hidden', !isLoading);
      }

      setLoadingFallback() {
        this.clearLoadingFallback();
        this.loadingFallbackTimeout = window.setTimeout(() => {
          this.setLoading(false);
          this.syncFromDom();
        }, 8000);
      }

      clearLoadingFallback() {
        if (!this.loadingFallbackTimeout) return;
        window.clearTimeout(this.loadingFallbackTimeout);
        this.loadingFallbackTimeout = undefined;
      }
    }
  );
}
